import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { getCached, setCached, buildCacheKey } from '../utils/productCache';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  // categories state removed
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'createdAt');
  const [priceRange, setPriceRange] = useState({
    min: searchParams.get('minPrice') || '',
    max: searchParams.get('maxPrice') || ''
  });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const searchTimerRef = useRef(null);

  // Debounce search input by 400ms so we don't fire an API call on every keystroke
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy, priceRange, debouncedSearch]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (sortBy !== 'createdAt') params.set('sort', sortBy);
    if (priceRange.min) params.set('minPrice', priceRange.min);
    if (priceRange.max) params.set('maxPrice', priceRange.max);
    if (debouncedSearch) params.set('search', debouncedSearch);

    setSearchParams(params);
  }, [selectedCategory, sortBy, priceRange, debouncedSearch, setSearchParams]);

  // Lock scroll when mobile filters are open
  useEffect(() => {
    if (showFilters) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [showFilters]);

  const fetchProducts = async (page = 1) => {
    try {
      const params = {
        page,
        limit: 12,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        sort: sortBy,
        minPrice: priceRange.min || undefined,
        maxPrice: priceRange.max || undefined,
        search: debouncedSearch || undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(key =>
        params[key] === undefined && delete params[key]
      );

      // 1. Check in-memory cache first
      const cacheKey = buildCacheKey(params);
      const cached = getCached(cacheKey);

      // If we have a cached result, show it immediately (optimistic UI)
      if (cached) {
        setProducts(cached.data.products);
        setPagination(cached.data.pagination);
        setIsLoading(false);
        setError(null);
        
        // If it's still fresh, skip the background fetch
        if (!cached.isStale) return;
      } else {
        // Only show spinner if we have NO cached data at all
        setIsLoading(true);
      }

      // 2. Fetch fresh data (in background if we already showed cached data)
      const response = await api.get('/products', { params });

      // Filter out flowers category from shop page
      const filteredProducts = response.data.products.filter(
        product => product.category?.toLowerCase() !== 'flowers'
      );

      const resultData = { products: filteredProducts, pagination: response.data.pagination };

      // Update state and cache
      setProducts(resultData.products);
      setPagination(resultData.pagination);
      setCached(cacheKey, resultData);
      setError(null);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
  };

  const handlePriceRangeChange = (field, value) => {
    setPriceRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSortBy('createdAt');
    setPriceRange({ min: '', max: '' });
    setSearchQuery('');
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories', count: null },
    { value: 'bouquets', label: 'Bouquets', count: null },
    { value: 'postcards', label: 'Postcards', count: null },
    { value: 'gifts', label: 'Gifts', count: null },
    { value: 'others', label: 'Others', count: null },
  ];

  const sortOptions = [
    { value: 'createdAt', label: 'Newest First' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'rating', label: 'Highest Rated' },
  ];

  return (
    <div className="min-h-screen transition-colors duration-200 relative overflow-hidden" style={{
      background: 'linear-gradient(180deg, #fefefe 0%, #f8fafc 50%, #f1f5f9 100%)'
    }}>
      {/* Organic Shapes */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-100/40 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-sage-100/30 to-transparent rounded-full blur-2xl translate-x-1/3 translate-y-1/3"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-gray-900 mb-2 sm:mb-4 tracking-tight">Shop flowers</h1>
          <p className="text-sm sm:text-base text-gray-600 font-light">Discover our beautiful collection of fresh flowers and arrangements</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 sm:gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white/60 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-xl border border-white/40 p-4 sm:p-6 sticky top-20 sm:top-24">
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full btn-outline flex items-center justify-between px-4 py-2.5 bg-white"
                >
                  <span className="font-normal flex items-center gap-2 text-gray-900">
                    <Filter className="w-4 h-4 text-emerald-600" />
                    Filter products
                    {selectedCategory !== 'all' && (
                      <span className="text-emerald-600 text-xs font-normal border-l border-gray-200 pl-2 ml-1">
                        {categoryOptions.find(c => c.value === selectedCategory)?.label}
                      </span>
                    )}
                    {(selectedCategory !== 'all' || priceRange.min || priceRange.max) && (
                      <span className="ml-1 bg-emerald-600 text-white text-[10px] font-normal px-2 py-0.5 rounded-full">
                        {[
                          selectedCategory !== 'all',
                          priceRange.min !== '',
                          priceRange.max !== ''
                        ].filter(Boolean).length}
                      </span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>



              {/* Desktop Filter Sidebar */}
              <div className={`space-y-6 hidden lg:block`}>
                {/* Categories */}
                <div>
                  <h4 className="text-sm sm:text-base font-normal text-gray-900 mb-3 uppercase tracking-wider">Categories</h4>
                  <div className="space-y-2">
                    {categoryOptions.map((category) => (
                      <label key={category.value} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          value={category.value}
                          checked={selectedCategory === category.value}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          className="mr-2 text-yellow-600 focus:ring-yellow-500"
                        />
                        <span className="text-xs sm:text-sm text-gray-700">{category.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h4 className="text-sm sm:text-base font-normal text-gray-900 mb-3 uppercase tracking-wider">Price Range</h4>
                  <div className="space-y-2">
                    <input
                      id="desktop-min-price"
                      name="minPrice"
                      type="number"
                      placeholder="Min price"
                      value={priceRange.min}
                      onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                      className="input w-full"
                    />
                    <input
                      id="desktop-max-price"
                      name="maxPrice"
                      type="number"
                      placeholder="Max price"
                      value={priceRange.max}
                      onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                      className="input w-full"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                <button
                  onClick={clearFilters}
                  className="w-full btn-outline text-sm"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Search Bar and Sort Options */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                {/* Search Bar */}
                <input
                  id="product-search"
                  name="search"
                  type="text"
                  placeholder="Search for flowers..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="input w-full sm:max-w-md"
                />


              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="input pr-8 appearance-none cursor-pointer min-w-[180px]"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Products */}
            {isLoading ? (
              <LoadingSpinner text="Loading products..." />
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg mb-4">{error}</p>
                <button
                  onClick={() => fetchProducts()}
                  className="btn-primary"
                >
                  Try again
                </button>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">No products found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="btn-primary mt-4"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 py-8">
                  {products.map((product, index) => (
                    <div key={product._id} className="group">
                      <div className="bg-white/50 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-1 sm:p-2 shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:bg-white/60">
                        <ProductCard product={product} priority={index < 4} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <div className="flex space-x-2">
                      {pagination.hasPrev && (
                        <button
                          onClick={() => fetchProducts(pagination.currentPage - 1)}
                          className="btn-outline px-4 py-2"
                        >
                          Previous
                        </button>
                      )}

                      {[...Array(pagination.totalPages)].map((_, index) => {
                        const page = index + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => fetchProducts(page)}
                            className={`px-4 py-2 rounded-md ${page === pagination.currentPage
                              ? 'bg-emerald-600 text-white'
                              : 'btn-outline'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      {pagination.hasNext && (
                        <button
                          onClick={() => fetchProducts(pagination.currentPage + 1)}
                          className="btn-outline px-4 py-2"
                        >
                          Next
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Filter Sidebar (Moved to root) */}
      {showFilters && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setShowFilters(false)}
          />

          <div className="absolute right-0 top-0 h-full w-[90%] sm:w-[400px] bg-white shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-normal text-gray-900 mb-4 lg:mb-6 leading-tight tracking-tight">
                  Filter products
                </h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-8">
                {/* Categories */}
                <div>
                  <h4 className="text-base font-bold font-serif-elegant text-gray-900 mb-4">Categories</h4>
                  <div className="space-y-3">
                    {categoryOptions.map((category) => (
                      <label key={category.value} className="flex items-center cursor-pointer group">
                        <div className="relative flex items-center">
                          <input
                            type="radio"
                            name="mobile-category"
                            value={category.value}
                            checked={selectedCategory === category.value}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            className="peer sr-only"
                          />
                          <div className="w-5 h-5 border-2 border-gray-300 rounded-full peer-checked:border-emerald-600 peer-checked:border-[6px] transition-all duration-200"></div>
                          <span className="ml-3 text-gray-600 group-hover:text-emerald-700 transition-colors peer-checked:text-gray-900 peer-checked:font-medium">
                            {category.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h4 className="text-base font-bold font-serif-elegant text-gray-900 mb-4">Price Range</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs text-gray-500 uppercase tracking-wider">Min Price</label>
                      <input
                        id="mobile-min-price"
                        name="minPrice"
                        type="number"
                        placeholder="0"
                        value={priceRange.min}
                        onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                        className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all bg-gray-50"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-500 uppercase tracking-wider">Max Price</label>
                      <input
                        id="mobile-max-price"
                        name="maxPrice"
                        type="number"
                        placeholder="Any"
                        value={priceRange.max}
                        onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                        className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 space-y-3">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-emerald-600 text-white font-bold h-12 rounded-xl hover:bg-emerald-700 active:scale-[0.98] transition-all shadow-lg shadow-emerald-600/20"
                  >
                    Apply filters
                  </button>
                  <button
                    onClick={clearFilters}
                    className="w-full bg-white text-gray-500 font-medium h-12 rounded-xl border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    Reset all
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
